import { useRef, useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Link, useNavigate, useParams } from "react-router-dom"
import { Badge, Button, Input } from "@/components/ui"
import { fehlerNachricht, useToast } from "@/lib/toast"
import {
  EREIGNISSTATUS_LABEL,
  EREIGNISTYP_LABEL,
  erstelleTicketAusEreignis,
  fetchEreignis,
  fetchKenntnisnahmen,
  kenntnisnahmeHinzufuegen,
  uploadEreignisBild,
} from "@/lib/api"

/** Ereignis-Detail (AP-4.4, 10-F5/F9): Ticket-Erzeugung, Bild-Upload, Kenntnisnahme.
 *
 * Route-Wrapper (Deep-Link-Fallback, `/ereignis/:id`) — liest die ID aus der URL
 * und delegiert an `EreignisDetailContent`. Primärer Aufrufweg ist das Sheet aus
 * `EreignisPage` (UX_KONZEPT.md §7). */
export function EreignisDetailPage() {
  const params = useParams<{ id: string }>()
  const ereignisId = Number(params.id)
  return (
    <div>
      <Link to="/ereignis" className="mb-2 inline-block text-sm text-slate-500 hover:underline">
        ← zurück zur Liste
      </Link>
      <EreignisDetailContent ereignisId={ereignisId} />
    </div>
  )
}

/** Inhalt der Ereignis-Detailansicht — wird von der Route (Deep-Link-Fallback)
 * und vom Sheet (`EreignisPage`) genutzt. */
export function EreignisDetailContent({ ereignisId }: { ereignisId: number }) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const toast = useToast()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [neueKenntnisnahme, setNeueKenntnisnahme] = useState("")

  const { data: ereignis, isLoading } = useQuery({
    queryKey: ["ereignisse", ereignisId],
    queryFn: () => fetchEreignis(ereignisId),
  })
  const { data: kenntnisnahmen } = useQuery({
    queryKey: ["ereignis-kenntnisnahmen", ereignisId],
    queryFn: () => fetchKenntnisnahmen(ereignisId),
  })

  const invalidieren = () => queryClient.invalidateQueries({ queryKey: ["ereignisse", ereignisId] })

  const ticketErstellen = useMutation({
    mutationFn: () => erstelleTicketAusEreignis(ereignisId),
    onSuccess: (ticket) => {
      invalidieren()
      toast.success("Serviceticket erstellt.")
      navigate(`/tickets/${ticket.id}`)
    },
    onError: (err) => toast.error(fehlerNachricht(err, "Serviceticket konnte nicht erstellt werden.")),
  })

  const bildHochladen = useMutation({
    mutationFn: (file: File) => uploadEreignisBild(ereignisId, file),
    onSuccess: () => {
      invalidieren()
      toast.success("Bild hochgeladen.")
    },
    onError: (err) => toast.error(fehlerNachricht(err, "Bild konnte nicht hochgeladen werden.")),
  })

  const kenntnisnahmeErgaenzen = useMutation({
    mutationFn: () => kenntnisnahmeHinzufuegen(ereignisId, neueKenntnisnahme),
    onSuccess: () => {
      setNeueKenntnisnahme("")
      queryClient.invalidateQueries({ queryKey: ["ereignis-kenntnisnahmen", ereignisId] })
      toast.success("Kenntnisnahme hinzugefügt.")
    },
    onError: (err) => toast.error(fehlerNachricht(err, "Kenntnisnahme konnte nicht hinzugefügt werden.")),
  })

  if (isLoading || !ereignis) {
    return (
      <div className="animate-pulse space-y-3" aria-label="Lade Ereignis …">
        <div className="h-4 w-32 rounded-md bg-slate-100" />
        <div className="h-8 w-80 rounded-md bg-slate-100" />
        <div className="h-40 rounded-md bg-slate-100" />
      </div>
    )
  }

  return (
    <div>
      <header className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">{ereignis.ereignisIdAnzeige}</h1>
          <p className="mt-1 text-sm text-slate-600">{ereignis.kurzbeschreibung}</p>
        </div>
        <Badge tone="slate">{EREIGNISSTATUS_LABEL[ereignis.ereignisstatus]}</Badge>
      </header>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="rounded-lg border border-slate-200 bg-white p-4">
          <h2 className="mb-3 text-sm font-semibold text-slate-900">Details</h2>
          <dl className="space-y-2 text-sm">
            <Row label="Ereignistyp" wert={EREIGNISTYP_LABEL[ereignis.ereignistyp]} />
            <Row label="Zeitpunkt" wert={new Date(ereignis.zeitpunkt).toLocaleString("de-DE")} />
            <Row label="Meldende Person" wert={ereignis.melderEmployeeId} />
            <Row label="Anlage-ID" wert={String(ereignis.anlageId)} />
            <Row label="Anzahl Servicetickets" wert={String(ereignis.anzahlServicetickets)} />
          </dl>
          {ereignis.beschreibung && (
            <p className="mt-3 whitespace-pre-wrap text-sm text-slate-700">{ereignis.beschreibung}</p>
          )}
        </div>

        <div className="rounded-lg border border-slate-200 bg-white p-4">
          <h2 className="mb-3 text-sm font-semibold text-slate-900">
            Serviceticket erstellen (R-A-14)
          </h2>
          <p className="mb-3 text-xs text-slate-500">
            Störungen erhalten automatisch Priorität „hoch".
          </p>
          <Button
            type="button"
            variant="primary"
            onClick={() => ticketErstellen.mutate()}
            disabled={ticketErstellen.isPending}
          >
            Serviceticket erstellen
          </Button>
          {ticketErstellen.isError && (
            <p className="mt-2 text-sm text-red-700">Erstellung fehlgeschlagen.</p>
          )}

          <h2 className="mb-3 mt-6 text-sm font-semibold text-slate-900">Bild (10-A.Y1)</h2>
          {ereignis.bildPaperlessId ? (
            <p className="text-xs text-slate-500">Hochgeladen (Paperless-ID: {ereignis.bildPaperlessId})</p>
          ) : (
            <p className="text-xs text-slate-400">Noch kein Bild hochgeladen.</p>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="mt-2 rounded-md text-xs focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1 focus-visible:outline-none"
            onChange={(e) => {
              const file = e.target.files?.[0]
              if (file) bildHochladen.mutate(file)
            }}
          />
        </div>

        <div className="rounded-lg border border-slate-200 bg-white p-4 sm:col-span-2">
          <h2 className="mb-3 text-sm font-semibold text-slate-900">
            Zur Kenntnis genommen (10-F9)
          </h2>
          <form
            className="mb-3 flex gap-2"
            onSubmit={(e) => {
              e.preventDefault()
              if (neueKenntnisnahme.trim()) kenntnisnahmeErgaenzen.mutate()
            }}
          >
            <Input
              type="text"
              value={neueKenntnisnahme}
              onChange={(e) => setNeueKenntnisnahme(e.target.value)}
              placeholder="employee_id"
              className="flex-1"
            />
            <Button type="submit" variant="primary">
              Hinzufügen
            </Button>
          </form>
          <ul className="flex flex-wrap gap-2 text-sm">
            {(kenntnisnahmen ?? []).map((k) => (
              <li key={k.id} className="rounded-full bg-slate-100 px-3 py-1 text-slate-700">
                {k.employeeId}
              </li>
            ))}
            {(kenntnisnahmen ?? []).length === 0 && (
              <li className="text-slate-400">Noch keine Kenntnisnahme.</li>
            )}
          </ul>
        </div>
      </div>

      <p className="mt-6 text-xs text-slate-400">
        <Link to="/ereignis/dashboard" className="hover:underline">
          📊 Zum Ereignis-Dashboard
        </Link>
      </p>
    </div>
  )
}

function Row({ label, wert }: { label: string; wert: string }) {
  return (
    <div className="flex justify-between border-b border-slate-100 pb-1">
      <dt className="text-slate-500">{label}</dt>
      <dd className="font-medium text-slate-900">{wert}</dd>
    </div>
  )
}
